import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';

import type { AvisosGavi } from '#components/mobile/avisos/avisos';
import { leerTravesia } from '#components/mobile/travesia/estado';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { usePlan } from '#hooks/usePlan';
import { useSelector } from '#redux';

import {
  avisosPruebaMovil,
  claveHito,
  finPrueba,
  leerPrefPrueba,
  situacionRumbo,
} from './avisosPrueba';
import type { AvisoPruebaMovil } from './avisosPrueba';

/**
 * Avisos de la prueba para el móvil con la app cerrada: los textos se
 * escriben ahora (con lo que tiene Rumbo hoy) y el service worker los da en
 * vez de un toque el día que toca. `clave` cambia cuando cambia algo que los
 * textos usan, para volver a escribirlos.
 */
export function usePruebaMovil() {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const { plan } = usePlan();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const travesiaRaw = useSelector(state => state.prefs.synced.travesia);
  const prefRaw = useSelector(state => state.prefs.synced['rumbo-prueba']);

  const enPrueba =
    plan?.estado === 'prueba' && plan.gratisHasta != null && plan.precios;
  const travesia = leerTravesia(travesiaRaw);
  const pref = leerPrefPrueba(prefRaw);
  const idGuardado = pref.id ?? travesia?.ids?.rumbo?.id;
  const id = categorias.some(c => c.id === idGuardado && !c.hidden)
    ? (idGuardado ?? null)
    : null;

  async function calcular(avisos: AvisosGavi): Promise<AvisoPruebaMovil[]> {
    if (!enPrueba || plan?.gratisHasta == null || !plan.precios) {
      return [];
    }
    const fin = finPrueba(plan.gratisHasta);
    const precio = plan.precios.mensual;
    let saldo: number | null = null;
    if (id) {
      try {
        const celda = await send('get-cell', {
          sheetName: monthUtils.sheetForMonth(monthUtils.monthFromDate(fin)),
          name: `leftover-${id}`,
        });
        saldo = typeof celda?.value === 'number' ? celda.value : 0;
      } catch {
        saldo = 0;
      }
    }
    const situacion = situacionRumbo(id != null, saldo, precio);
    const eur = (c: number) => format(c, 'financial');
    return avisosPruebaMovil(t, {
      fin,
      hoy: monthUtils.currentDay(),
      situacion,
      formatos: {
        fecha: new Intl.DateTimeFormat(i18n.language, {
          day: 'numeric',
          month: 'long',
        }).format(new Date(`${fin}T12:00:00`)),
        precio: eur(precio),
        ...(situacion.tipo === 'falta'
          ? { apartado: eur(situacion.apartado), falta: eur(situacion.falta) }
          : {}),
      },
      planificado:
        travesia?.ids?.rumbo?.id === id &&
        !travesia?.ids?.rumbo?.pendiente &&
        !pref.visto.includes(claveHito(fin, 'ingreso')),
      tranquila: avisos.tono === 'tranquila',
      cifras: avisos.cifras,
      pref,
    });
  }

  return {
    calcular,
    clave: enPrueba ? `${plan?.gratisHasta}:${id ?? ''}:${prefRaw ?? ''}` : '',
  };
}
